/**
 * Inspection Hooks
 *
 * Provides hooks for DVI inspection operations with TanStack Query.
 */

import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';
import { fromOffsetResponse, usePagedQuery } from './usePagedQuery';

// ============================================================================
// TYPES
// ============================================================================

export type InspectionStatus =
  | 'draft'
  | 'pending'
  | 'in_progress'
  | 'completed'
  | 'sent_to_customer'
  | 'approved'
  | 'rejected';

export type ItemCondition = 'good' | 'fair' | 'poor' | 'critical' | null;
export type RecommendationUrgency = 'immediate' | 'soon' | 'future';

// Multi-measurement types
export interface MeasurementDefinition {
  id: string;
  label: string;
  unit: string;
  min?: number;
  warningMin?: number;
  max?: number;
  sortOrder: number;
}

export interface SubItemDefinition {
  id: string;
  label: string;
  sortOrder: number;
}

export interface MeasurementValueEntry {
  subItemId?: string;
  measurementId: string;
  value: number;
}

// Template types
export interface CategoryItem {
  id: string;
  name: string;
  description?: string;
  requiresPhoto?: boolean;
  hasMeasurement?: boolean;
  measurementUnit?: string;
  measurements?: MeasurementDefinition[];
  subItems?: SubItemDefinition[];
  sortOrder: number;
}

export interface Category {
  id: string;
  name: string;
  description?: string;
  sortOrder: number;
  items: CategoryItem[];
}

export interface InspectionTemplate {
  id: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  isBase: boolean;
  isActive: boolean;
  categories: Category[];
  vehicleTypes: string[] | null;
  serviceTypes: string[] | null;
  vehicleMakes: string[] | null;
  vehicleModels: string[] | null;
  sourceTemplateId: string | null;
  version: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

// Inspection item types
export interface InspectionItem {
  id: string;
  inspectionId: string;
  categoryId: string;
  categoryName: string;
  itemName: string;
  itemDescription: string | null;
  condition: ItemCondition;
  severity: string | null;
  notes: string | null;
  measurementValue: string | null;
  measurementUnit: string | null;
  measurementValues: MeasurementValueEntry[];
  subItemsConfig: SubItemDefinition[] | null;
  measurementsConfig: MeasurementDefinition[] | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

// Photo types
export interface InspectionPhoto {
  id: string;
  inspectionId: string;
  inspectionItemId: string | null;
  fileKey: string;
  fileUrl: string;
  thumbnailUrl: string | null;
  fileType: 'image' | 'video';
  mimeType: string;
  fileSizeBytes: number | null;
  caption: string | null;
  uploadedBy: string;
  createdAt: string;
}

// Recommendation types
export interface InspectionRecommendation {
  id: string;
  inspectionId: string;
  inspectionItemId: string | null;
  description: string;
  customerDescription: string | null;
  urgency: RecommendationUrgency;
  partsCostCents: number;
  laborCostCents: number;
  laborHours: number | null;
  vatPercent: number;
  totalCostCents: number;
  approvalRequestId: string | null;
  approvalStatus: string | null;
  approvedAt: string | null;
  workOrderItemId: string | null;
  offerId?: string | null;
  createdAt: string;
  updatedAt: string;
}

// Report types
export interface InspectionReport {
  id: string;
  inspectionId: string;
  reportType: 'customer' | 'internal' | 'summary';
  language: 'fi' | 'sv' | 'en';
  fileKey: string | null;
  fileUrl: string | null;
  sentVia: string | null;
  sentAt: string | null;
  viewedAt: string | null;
  createdAt: string;
}

// Cost estimation types
export interface PartEstimate {
  name: string;
  quantity: number;
  unitCostCents: number;
  unitSellCents: number;
}

export interface LaborEstimate {
  minutes: number;
  description: string;
}

export interface CostEstimate {
  categoryId: string;
  itemId: string;
  itemName: string;
  condition: 'good' | 'fair' | 'poor' | 'critical';
  urgency: RecommendationUrgency;
  description: string;
  customerDescription: string;
  parts: PartEstimate[];
  labor: LaborEstimate;
  partsCostCents: number;
  partsSellCents: number;
  laborCostCents: number;
  subtotalCents: number;
  vatCents: number;
  totalCents: number;
}

export interface LaborRateInfo {
  hourlyRateCents: number;
  vatPercent: number;
  minimumMinutes: number;
}

export interface CostEstimateResponse {
  data: CostEstimate;
  laborRateInfo: LaborRateInfo;
}

export interface CostEstimateBatchResponse {
  data: CostEstimate[];
  summary: {
    estimatesCount: number;
    findingsWithNoEstimate: number;
    totalPartsCents: number;
    totalLaborCents: number;
    totalSubtotalCents: number;
    totalVatCents: number;
    grandTotalCents: number;
  };
  laborRateInfo: LaborRateInfo;
}

// List item types
export interface InspectionListItem {
  workOrderNumber?: string | null;
  id: string;
  bookingId: string;
  mechanicId: string;
  templateId: string | null;
  status: InspectionStatus;
  vehicleMileage: number | null;
  customerNotes: string | null;
  internalNotes: string | null;
  totalIssues: number;
  totalCritical: number;
  totalRecommendationsCents: number;
  approvedRecommendationsCents: number;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  booking: {
    serviceType: string | null;
    scheduledAt: string | null;
  };
  vehicle: {
    registrationNumber: string | null;
    make: string | null;
    model: string | null;
  };
  customer: {
    firstName: string | null;
    lastName: string | null;
  };
  mechanic: {
    firstName: string | null;
    lastName: string | null;
  };
}

// Detail types
export interface InspectionDetail extends Omit<InspectionListItem, 'vehicle' | 'customer'> {
  vehicle: {
    id: string | null;
    registrationNumber: string | null;
    make: string | null;
    model: string | null;
    year: number | null;
  };
  customer: {
    id: string | null;
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
  };
  items: InspectionItem[];
  photos: InspectionPhoto[];
  recommendations: InspectionRecommendation[];
}

// Stats response from GET /inspections/stats
export interface InspectionStats {
  totalCount: number;
  byStatus: { status: string; count: number }[];
  criticalIssuesCount: number;
  pendingRecommendationsCents: number;
}

// Filter types
export interface InspectionFilters {
  q?: string;
  status?: InspectionStatus;
  mechanicId?: string;
  bookingId?: string;
  limit?: number;
  cursor?: string;
}

/** Columns the offset list sorts. Joined vehicle and customer names are not included. */
export type InspectionListSortField = 'status' | 'createdAt' | 'totalIssues';

/**
 * Filters for the paged inspections list. `status` is one status or a
 * comma-separated list; omit it to include every status.
 */
export interface InspectionPageFilters {
  q?: string;
  status?: string;
  mechanicId?: string;
  bookingId?: string;
}

export interface TemplateFilters {
  includeInactive?: boolean;
  limit?: number;
  cursor?: string;
}

// API response types
interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    nextCursor: string | null;
    prevCursor: string | null;
    hasMore: boolean;
  };
}

// ============================================================================
// TEMPLATE HOOKS
// ============================================================================

/**
 * Fetch inspection templates list
 */
const EMPTY_TEMPLATE_FILTERS: TemplateFilters = {};
export function useInspectionTemplates(filters: TemplateFilters = EMPTY_TEMPLATE_FILTERS) {
  return useQuery({
    queryKey: queryKeys.inspectionTemplates.list(filters as Record<string, unknown>),
    queryFn: () =>
      api.get<PaginatedResponse<InspectionTemplate>>('/inspection-templates', {
        params: {
          includeInactive: filters.includeInactive?.toString(),
          limit: filters.limit,
          cursor: filters.cursor,
        },
      }),
    retry: 1,
    staleTime: 60 * 1000,
  });
}

/**
 * Fetch single inspection template
 */
export function useInspectionTemplate(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.inspectionTemplates.detail(id ?? ''),
    queryFn: () => api.get<InspectionTemplate>(`/inspection-templates/${id}`),
    enabled: !!id,
  });
}

/**
 * Create inspection template mutation
 */
export function useCreateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      name: string;
      description?: string;
      categories: Category[];
      vehicleTypes?: string[];
      serviceTypes?: string[];
      isDefault?: boolean;
    }) => api.post<InspectionTemplate>('/inspection-templates', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inspectionTemplates.all });
    },
  });
}

/**
 * Update inspection template mutation
 */
export function useUpdateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string;
      name?: string;
      description?: string;
      categories?: Category[];
      vehicleTypes?: string[];
      serviceTypes?: string[];
      isDefault?: boolean;
      isActive?: boolean;
    }) => api.patch<InspectionTemplate>(`/inspection-templates/${id}`, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inspectionTemplates.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.inspectionTemplates.detail(variables.id),
      });
    },
  });
}

/**
 * Clone inspection template mutation
 */
export function useCloneTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.post<InspectionTemplate>(`/inspection-templates/${id}/clone`, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inspectionTemplates.all });
    },
  });
}

/**
 * Delete inspection template mutation
 */
export function useDeleteTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/inspection-templates/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inspectionTemplates.all });
    },
  });
}

// ============================================================================
// INSPECTION HOOKS
// ============================================================================

/**
 * Hook to fetch inspection statistics
 */
export function useInspectionStats() {
  return useQuery({
    queryKey: queryKeys.inspections.stats(),
    queryFn: () => api.get<InspectionStats>('/inspections/stats'),
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Fetch inspections list with infinite scrolling
 */
export function useInspections(
  filters: Omit<InspectionFilters, 'cursor'> = {},
  options?: { enabled?: boolean },
) {
  return useInfiniteQuery({
    queryKey: queryKeys.inspections.list(filters as Record<string, unknown>),
    queryFn: ({ pageParam }) =>
      api.get<PaginatedResponse<InspectionListItem>>('/inspections', {
        params: {
          q: filters.q,
          status: filters.status,
          mechanicId: filters.mechanicId,
          bookingId: filters.bookingId,
          limit: filters.limit ?? 20,
          cursor: pageParam,
        },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasMore ? lastPage.pagination.nextCursor : undefined,
    enabled: options?.enabled ?? true,
  });
}

interface OffsetInspectionListResponse {
  data: InspectionListItem[];
  pagination: { total: number };
}

/**
 * Page-number inspections list (#2742). Always sends `limit` and `offset`
 * (including 0) so the route selects offset mode. The infinite hook above
 * stays for the other consumers.
 */
export function usePagedInspections(
  filters: InspectionPageFilters,
  options?: { enabled?: boolean },
) {
  return usePagedQuery({
    queryKey: [...queryKeys.inspections.lists(), 'page'],
    filters,
    defaultSort: { sortBy: 'createdAt', sortDir: 'desc' },
    fetchPage: async ({ filters: pageFilters, limit, offset, sortBy, sortDir }) => {
      const params: {
        limit: number;
        offset: number;
        q?: string;
        status?: string;
        mechanicId?: string;
        bookingId?: string;
        sortBy?: string;
        sortDir?: 'asc' | 'desc';
      } = { limit, offset };
      if (pageFilters.q !== undefined) params.q = pageFilters.q;
      if (pageFilters.status !== undefined) params.status = pageFilters.status;
      if (pageFilters.mechanicId !== undefined) params.mechanicId = pageFilters.mechanicId;
      if (pageFilters.bookingId !== undefined) params.bookingId = pageFilters.bookingId;
      if (sortBy !== undefined) params.sortBy = sortBy;
      if (sortDir !== undefined) params.sortDir = sortDir;
      const res = await api.get<OffsetInspectionListResponse>('/inspections', { params });
      return fromOffsetResponse(res);
    },
    ...(options?.enabled !== undefined ? { enabled: options.enabled } : {}),
  });
}

/**
 * Fetch inspections for a specific booking (simple query, not infinite)
 */
export function useBookingInspections(bookingId: string | undefined) {
  return useQuery({
    queryKey: ['inspections', 'byBooking', bookingId] as const,
    queryFn: () =>
      api.get<PaginatedResponse<InspectionListItem>>('/inspections', {
        params: { bookingId, limit: 50 },
      }),
    enabled: !!bookingId,
    select: (data) => data.data,
    retry: 1,
    staleTime: 60 * 1000,
  });
}

/**
 * Create inspection from template in one step (like checklists)
 */
export function useCreateInspectionFromTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { bookingId: string; templateId: string }) =>
      api.post<InspectionListItem>('/inspections/from-template', data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['inspections', 'byBooking', variables.bookingId],
      });
    },
  });
}

/**
 * Fetch single inspection with all details
 */
export function useInspection(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.inspections.detail(id ?? ''),
    queryFn: () => api.get<InspectionDetail>(`/inspections/${id}`),
    enabled: !!id,
  });
}

/**
 * Create inspection mutation
 */
export function useCreateInspection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      bookingId: string;
      templateId?: string;
      mechanicId: string;
      vehicleMileage?: number;
      customerNotes?: string;
    }) => api.post<InspectionDetail>('/inspections', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inspections.all });
    },
  });
}

/**
 * Start inspection mutation (populate items from template)
 */
export function useStartInspection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, templateId }: { id: string; templateId?: string }) =>
      api.post<InspectionDetail>(`/inspections/${id}/start`, { templateId }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inspections.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.inspections.detail(variables.id),
      });
    },
  });
}

/**
 * Complete inspection mutation
 */
export function useCompleteInspection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, internalNotes }: { id: string; internalNotes?: string }) =>
      api.post<InspectionDetail>(`/inspections/${id}/complete`, { internalNotes }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inspections.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.inspections.detail(variables.id),
      });
    },
  });
}

/**
 * Delete inspection mutation
 */
export function useDeleteInspection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/inspections/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inspections.all });
    },
  });
}

// ============================================================================
// ITEM HOOKS
// ============================================================================

/**
 * Update inspection item condition mutation
 */
export function useUpdateItemCondition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      inspectionId,
      itemId,
      condition,
      notes,
      measurementValue,
      measurementValues,
    }: {
      inspectionId: string;
      itemId: string;
      condition: ItemCondition;
      notes?: string;
      measurementValue?: number | null;
      measurementValues?: MeasurementValueEntry[];
    }) =>
      api.patch<InspectionItem>(`/inspections/${inspectionId}/items/${itemId}`, {
        condition,
        notes,
        measurementValue,
        measurementValues,
      }),
    onSuccess: (data, variables) => {
      queryClient.setQueryData<InspectionDetail>(queryKeys.inspections.detail(variables.inspectionId), (current) =>
        current ? { ...current, items: current.items.map((item) => item.id === variables.itemId ? data : item) } : current,
      );
      // Invalidate the inspection detail to refresh items
      queryClient.invalidateQueries({
        queryKey: queryKeys.inspections.detail(variables.inspectionId),
      });
    },
  });
}

// ============================================================================
// PHOTO HOOKS
// ============================================================================

/**
 * Fetch photos for an inspection
 */
export function useInspectionPhotos(inspectionId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.inspections.photos(inspectionId ?? ''),
    queryFn: () => api.get<{ data: InspectionPhoto[] }>(`/inspections/${inspectionId}/photos`),
    enabled: !!inspectionId,
    select: (data) => data.data,
  });
}

/**
 * Register photo mutation
 */
export function useRegisterPhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      inspectionId,
      ...data
    }: {
      inspectionId: string;
      inspectionItemId?: string;
      fileKey: string;
      fileUrl: string;
      fileType: 'image' | 'video';
      mimeType: string;
      fileSizeBytes?: number;
      caption?: string;
    }) => api.post<InspectionPhoto>(`/inspections/${inspectionId}/photos`, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.inspections.photos(variables.inspectionId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.inspections.detail(variables.inspectionId),
      });
    },
  });
}

/**
 * Update photo mutation
 */
export function useUpdatePhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      inspectionId,
      photoId,
      caption,
      inspectionItemId,
    }: {
      inspectionId: string;
      photoId: string;
      caption?: string | null;
      inspectionItemId?: string | null;
    }) =>
      api.patch<InspectionPhoto>(`/inspections/${inspectionId}/photos/${photoId}`, {
        caption,
        inspectionItemId,
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.inspections.photos(variables.inspectionId),
      });
    },
  });
}

/**
 * Delete photo mutation
 */
export function useDeletePhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ inspectionId, photoId }: { inspectionId: string; photoId: string }) =>
      api.delete(`/inspections/${inspectionId}/photos/${photoId}`),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.inspections.photos(variables.inspectionId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.inspections.detail(variables.inspectionId),
      });
    },
  });
}

/**
 * Add photo mutation (full upload flow)
 *
 * 1. Get presigned upload URL
 * 2. Upload file to S3
 * 3. Register photo in database
 */
export function useAddPhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      inspectionId,
      itemId,
      file,
      caption,
    }: {
      inspectionId: string;
      itemId?: string;
      file: File;
      caption?: string;
    }) => {
      // 1. Get presigned upload URL
      const uploadResponse = await api.post<{
        uploadUrl: string;
        fileKey: string;
        publicUrl: string;
      }>(`/inspections/${inspectionId}/photos/upload-url`, {
        filename: file.name,
        mimeType: file.type,
        fileSizeBytes: file.size,
      });

      // 2. Upload to S3
      await fetch(uploadResponse.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      // 3. Register photo
      const photo = await api.post<InspectionPhoto>(`/inspections/${inspectionId}/photos`, {
        inspectionItemId: itemId,
        fileKey: uploadResponse.fileKey,
        fileUrl: uploadResponse.publicUrl,
        fileType: file.type.startsWith('video/') ? 'video' : 'image',
        mimeType: file.type,
        fileSizeBytes: file.size,
        caption,
      });

      return photo;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.inspections.photos(variables.inspectionId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.inspections.detail(variables.inspectionId),
      });
    },
  });
}

// ============================================================================
// RECOMMENDATION HOOKS
// ============================================================================

/**
 * Fetch recommendations for an inspection
 */
export function useInspectionRecommendations(inspectionId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.inspections.recommendations(inspectionId ?? ''),
    queryFn: () =>
      api.get<{ data: InspectionRecommendation[] }>(`/inspections/${inspectionId}/recommendations`),
    enabled: !!inspectionId,
    select: (data) => data.data,
  });
}

/**
 * Add recommendation mutation
 */
export function useAddRecommendation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      inspectionId,
      ...data
    }: {
      inspectionId: string;
      inspectionItemId?: string;
      description: string;
      customerDescription?: string;
      urgency: RecommendationUrgency;
      partsCostCents?: number;
      laborCostCents?: number;
      laborHours?: number;
    }) => api.post<InspectionRecommendation>(`/inspections/${inspectionId}/recommendations`, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.inspections.recommendations(variables.inspectionId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.inspections.detail(variables.inspectionId),
      });
    },
  });
}

/**
 * Update recommendation mutation
 */
export function useUpdateRecommendation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      inspectionId,
      recId,
      ...data
    }: {
      inspectionId: string;
      recId: string;
      inspectionItemId?: string;
      description?: string;
      customerDescription?: string;
      urgency?: RecommendationUrgency;
      partsCostCents?: number;
      laborCostCents?: number;
      laborHours?: number;
    }) =>
      api.patch<InspectionRecommendation>(
        `/inspections/${inspectionId}/recommendations/${recId}`,
        data
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.inspections.recommendations(variables.inspectionId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.inspections.detail(variables.inspectionId),
      });
    },
  });
}

/**
 * Delete recommendation mutation
 */
export function useDeleteRecommendation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ inspectionId, recId }: { inspectionId: string; recId: string }) =>
      api.delete(`/inspections/${inspectionId}/recommendations/${recId}`),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.inspections.recommendations(variables.inspectionId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.inspections.detail(variables.inspectionId),
      });
    },
  });
}

// ============================================================================
// REPORT HOOKS
// ============================================================================

/**
 * Fetch reports for an inspection
 */
export function useInspectionReports(inspectionId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.inspections.reports(inspectionId ?? ''),
    queryFn: () => api.get<{ data: InspectionReport[] }>(`/inspections/${inspectionId}/reports`),
    enabled: !!inspectionId,
    select: (data) => data.data,
  });
}

/**
 * Generate report mutation
 */
export function useGenerateReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      inspectionId,
      reportType,
      language,
    }: {
      inspectionId: string;
      reportType: 'customer' | 'internal' | 'summary';
      language?: 'fi' | 'sv' | 'en';
    }) => api.post<InspectionReport>(`/inspections/${inspectionId}/reports`, { reportType, language }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.inspections.reports(variables.inspectionId),
      });
    },
  });
}

/**
 * Send report mutation
 */
export interface SendReportParams {
  inspectionId: string;
  reportId: string;
  channel: 'whatsapp' | 'email';
  recipientPhone?: string;
  recipientEmail?: string;
  message?: string;
}

export interface SendReportResult {
  success: boolean;
  reportId: string;
  channel: 'whatsapp' | 'email';
  sentAt: string;
  message?: string;
}

export function useSendReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      inspectionId,
      reportId,
      channel,
      recipientPhone,
      recipientEmail,
      message,
    }: SendReportParams) =>
      api.post<SendReportResult>(`/inspections/${inspectionId}/reports/${reportId}/send`, {
        channel,
        recipientPhone,
        recipientEmail,
        message,
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.inspections.reports(variables.inspectionId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.inspections.detail(variables.inspectionId),
      });
    },
  });
}

// ============================================================================
// WORK ORDER LINKING HOOKS
// ============================================================================

/**
 * Link a recommendation to an existing work order item
 */
export function useLinkRecommendationToWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      inspectionId,
      recId,
      workOrderItemId,
    }: {
      inspectionId: string;
      recId: string;
      workOrderItemId: string;
    }) =>
      api.patch<InspectionRecommendation>(
        `/inspections/${inspectionId}/recommendations/${recId}`,
        { workOrderItemId }
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.inspections.recommendations(variables.inspectionId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.inspections.detail(variables.inspectionId),
      });
    },
  });
}

/**
 * Unlink a recommendation from a work order item
 */
export function useUnlinkRecommendationFromWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      inspectionId,
      recId,
    }: {
      inspectionId: string;
      recId: string;
    }) =>
      api.patch<InspectionRecommendation>(
        `/inspections/${inspectionId}/recommendations/${recId}`,
        { workOrderItemId: null }
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.inspections.recommendations(variables.inspectionId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.inspections.detail(variables.inspectionId),
      });
    },
  });
}

/**
 * Create a work order item from a recommendation and link them
 */
export function useCreateWorkOrderItemFromRecommendation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      inspectionId,
      recId,
      workOrderId,
      recommendation,
    }: {
      inspectionId: string;
      recId: string;
      workOrderId: string;
      recommendation: {
        description: string;
        partsCostCents: number;
        laborCostCents: number;
        laborHours?: number;
      };
    }) => {
      // 1. Create work order item
      const workOrderItem = await api.post<{ id: string }>(`/work-orders/${workOrderId}/items`, {
        description: recommendation.description,
        partsCostCents: recommendation.partsCostCents,
        laborCostCents: recommendation.laborCostCents,
        estimatedHours: recommendation.laborHours,
        approvalRequired: true,
        category: 'inspection_finding',
      });

      // 2. Link recommendation to the new item
      await api.patch<InspectionRecommendation>(
        `/inspections/${inspectionId}/recommendations/${recId}`,
        { workOrderItemId: workOrderItem.id }
      );

      return workOrderItem;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.inspections.recommendations(variables.inspectionId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.inspections.detail(variables.inspectionId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.workOrders.detail(variables.workOrderId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.workOrders.lists(),
      });
    },
  });
}

// ============================================================================
// COST ESTIMATION HOOKS
// ============================================================================

/**
 * Estimate cost for a single finding mutation
 */
export function useEstimateCost() {
  return useMutation({
    mutationFn: (data: {
      categoryId: string;
      itemId: string;
      condition: 'good' | 'fair' | 'poor' | 'critical';
      vehicleMake?: string;
      vehicleModel?: string;
    }) => api.post<CostEstimateResponse>('/inspections/estimate-cost', data),
  });
}

/**
 * Estimate costs for multiple findings mutation
 */
export function useEstimateCostsBatch() {
  return useMutation({
    mutationFn: (data: {
      findings: {
        categoryId: string;
        itemId: string;
        condition: 'good' | 'fair' | 'poor' | 'critical';
      }[];
      vehicleMake?: string;
      vehicleModel?: string;
    }) => api.post<CostEstimateBatchResponse>('/inspections/estimate-costs-batch', data),
  });
}
